/* TRUE AUTHENTIC — Custom Order Form
 * Prices always come from Shopify variants rendered by Liquid; Shopify re-prices every cart line server-side.
 * Artwork files are uploaded by Shopify's cart as line item properties (multipart /cart/add.js).
 */
(() => {
  'use strict';

  const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
  const FILE_EXT = /\.(png|jpe?g|webp|svg|pdf)$/i;
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const PHONE = /^[0-9+()\-.\s]{7,30}$/;

  const lines = (value) => String(value || '')
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

  const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  function requestId() {
    const d = new Date();
    const stamp = `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
    return `TA-${stamp}-${rand}`;
  }

  function init(root) {
    if (!root || root.dataset.taCoReady) return;
    root.dataset.taCoReady = 'true';

    let config;
    try {
      config = JSON.parse(root.querySelector('[data-ta-co-config]').textContent);
    } catch (error) {
      console.error('TA custom order: configuration could not be read', error);
      return;
    }

    const form = root.querySelector('[data-ta-co-form]');
    const el = (name) => root.querySelector(`[data-ta-co-${name}]`);
    const money = new Intl.NumberFormat(config.locale || 'en', { style: 'currency', currency: config.currency || 'USD' });
    const fmt = (cents) => money.format((Number(cents) || 0) / 100);
    const quoteProductReady = Boolean(config.quoteVariantId && config.quoteAvailable && Number(config.quoteVariantPrice) === 0);

    const state = { garment: null, color: '', qty: {}, method: '', placements: new Set(), files: [] };

    const garmentById = (id) => config.garments.find((g) => g.id === id) || null;

    function optionIndex(product, pattern) {
      if (!product) return -1;
      return product.options.findIndex((name) => pattern.test(String(name)));
    }

    function colorsFor(g) {
      const ci = optionIndex(g.product, /^(color|colour)$/i);
      if (ci >= 0) return [...new Set(g.product.variants.map((v) => v.options[ci]))];
      return lines(g.colors);
    }

    function sizesFor(g) {
      const si = optionIndex(g.product, /^size$/i);
      if (si >= 0) return [...new Set(g.product.variants.map((v) => v.options[si]))];
      return lines(g.sizes);
    }

    function variantFor(g, color, size) {
      if (!g.product) return null;
      const ci = optionIndex(g.product, /^(color|colour)$/i);
      const si = optionIndex(g.product, /^size$/i);
      return g.product.variants.find((v) => (ci < 0 || v.options[ci] === color) && (si < 0 || v.options[si] === size)) || null;
    }

    function chip(type, name, value, checked) {
      const label = document.createElement('label');
      label.className = 'ta-co__chip';
      const input = document.createElement('input');
      input.type = type;
      input.name = name;
      input.value = value;
      input.checked = Boolean(checked);
      const span = document.createElement('span');
      span.textContent = value;
      label.append(input, span);
      return label;
    }

    function renderGarment() {
      const g = state.garment;
      const details = el('garment-details');
      details.hidden = !g;
      el('design-options').hidden = !g;
      el('design-empty').hidden = Boolean(g);
      if (!g) return;
      el('other').hidden = !g.other;

      const colors = colorsFor(g);
      state.color = colors.length === 1 ? colors[0] : '';
      el('colors').replaceChildren(...colors.map((c) => chip('radio', 'color', c, c === state.color)));

      state.qty = {};
      const sizes = sizesFor(g);
      el('sizes').replaceChildren(...sizes.map((size) => {
        const wrap = document.createElement('label');
        wrap.className = 'ta-co__size';
        const name = document.createElement('span');
        name.textContent = size;
        const input = document.createElement('input');
        input.type = 'number';
        input.min = '0';
        input.max = '500';
        input.step = '1';
        input.inputMode = 'numeric';
        input.value = '0';
        input.dataset.size = size;
        input.setAttribute('aria-label', `Quantity in size ${size}`);
        wrap.append(name, input);
        return wrap;
      }));

      const methods = lines(g.methods);
      state.method = methods.length === 1 ? methods[0] : '';
      el('methods').replaceChildren(...methods.map((m) => chip('radio', 'method', m, m === state.method)));

      state.placements = new Set();
      el('placements').replaceChildren(...lines(g.placements).map((p) => chip('checkbox', 'placement', p, false)));
      renderPreview();
    }

    function totalQty() {
      return Object.values(state.qty).reduce((sum, n) => sum + n, 0);
    }

    function pricing() {
      const g = state.garment;
      const reasons = [];
      if (!g) return { mode: 'none', reasons };
      const rows = [];
      let subtotal = 0;
      let priced = Boolean(g.product);
      if (!g.product) reasons.push(`${g.title} is priced by quote.`);
      if (priced && state.method && !lines(g.pricedMethods).includes(state.method)) {
        priced = false;
        reasons.push(`${state.method} is priced by quote for this garment.`);
      }
      if (priced && state.placements.size > g.pricedPlacements) {
        priced = false;
        reasons.push(`More than ${g.pricedPlacements} placement${g.pricedPlacements === 1 ? '' : 's'} is priced by quote.`);
      }
      if (priced && form.elements.design_help.checked) {
        priced = false;
        reasons.push('Design help is priced by quote.');
      }
      for (const [size, qty] of Object.entries(state.qty)) {
        if (!qty) continue;
        const variant = variantFor(g, state.color, size);
        if (priced && (!variant || !variant.available)) {
          priced = false;
          reasons.push(`${state.color || 'This color'} in ${size} isn't available to buy online right now.`);
        }
        rows.push({ size, qty, variant });
        if (variant) subtotal += variant.price * qty;
      }
      return { mode: priced ? 'priced' : 'quote', rows, subtotal, reasons };
    }

    function renderPricing() {
      el('qty-total').textContent = `Total pieces: ${totalQty()}`;
      const p = pricing();
      const panel = el('pricing');
      const submit = el('submit');
      const note = el('mode-note');
      if (p.mode === 'none') {
        panel.innerHTML = '<p class="ta-co__hint">Choose a garment, color and quantities to see your options.</p>';
        submit.disabled = true;
        submit.textContent = 'Choose your garment';
        note.textContent = '';
        return p;
      }
      submit.disabled = false;
      if (p.mode === 'priced' && p.rows.length && state.color && state.method && state.placements.size) {
        panel.innerHTML = `
          <table class="ta-co__table">
            <thead><tr><th scope="col">Size</th><th scope="col">Qty</th><th scope="col">Each</th><th scope="col">Total</th></tr></thead>
            <tbody>${p.rows.map((r) => `<tr><td>${escapeHtml(r.size)}</td><td>${r.qty}</td><td>${fmt(r.variant.price)}</td><td>${fmt(r.variant.price * r.qty)}</td></tr>`).join('')}</tbody>
            <tfoot><tr><th scope="row" colspan="3">Subtotal before discounts</th><td>${fmt(p.subtotal)}</td></tr></tfoot>
          </table>`;
        submit.textContent = `Add to cart — ${fmt(p.subtotal)}`;
        note.textContent = config.pricedNote || '';
      } else if (p.mode === 'priced') {
        panel.innerHTML = '<p class="ta-co__hint">Choose a color, quantities, decoration and placement to see your price.</p>';
        submit.textContent = 'Add to cart';
        note.textContent = config.pricedNote || '';
      } else {
        panel.innerHTML = `
          <div class="ta-co__quote">
            <p class="ta-co__quote-title">Free quote request</p>
            <ul>${p.reasons.map((r) => `<li>${escapeHtml(r)}</li>`).join('')}</ul>
            <p>${totalQty()} piece${totalQty() === 1 ? '' : 's'} requested. Nothing is charged until you approve your price.</p>
          </div>`;
        submit.textContent = quoteProductReady ? 'Submit quote request' : 'Send quote request';
        note.textContent = quoteProductReady ? (config.quoteNote || '') : 'Quote requests are sent by email. Please reply to our confirmation email with your artwork files.';
      }
      return p;
    }

    function renderFiles() {
      const list = el('file-list');
      list.replaceChildren(...state.files.map((file) => {
        const li = document.createElement('li');
        li.textContent = `${file.name} — ${(file.size / 1048576).toFixed(1)} MB`;
        return li;
      }));
      renderPreview();
    }

    function placementPosition(placement) {
      const p = String(placement || '').toLowerCase();
      if (p.includes('left chest')) return { x: 64, y: 32, w: 14 };
      if (p.includes('right chest')) return { x: 36, y: 32, w: 14 };
      if (p.includes('left sleeve')) return { x: 82, y: 34, w: 10 };
      if (p.includes('right sleeve')) return { x: 18, y: 34, w: 10 };
      if (p.includes('neck')) return { x: 50, y: 18, w: 9 };
      if (p.includes('left leg')) return { x: 62, y: 45, w: 13 };
      if (p.includes('right leg')) return { x: 38, y: 45, w: 13 };
      if (p.includes('side')) return { x: 70, y: 48, w: 16 };
      if (p.includes('cuff')) return { x: 50, y: 70, w: 22 };
      return { x: 50, y: 40, w: 32 };
    }

    let previewUrl = null;
    function renderPreview() {
      const box = el('preview');
      const g = state.garment;
      const art = state.files.find((f) => IMAGE_TYPES.includes(f.type));
      if (previewUrl) { URL.revokeObjectURL(previewUrl); previewUrl = null; }
      if (!g || !g.image || !art) { box.hidden = true; return; }
      previewUrl = URL.createObjectURL(art);
      const pos = placementPosition([...state.placements][0]);
      el('preview-garment').src = g.image;
      const img = el('preview-art');
      img.src = previewUrl;
      img.style.left = `${pos.x}%`;
      img.style.top = `${pos.y}%`;
      img.style.width = `${pos.w}%`;
      box.hidden = false;
    }

    function value(name) {
      const field = form.elements[name];
      return field ? String(field.value || '').trim() : '';
    }

    function address(prefix) {
      return [value(`${prefix}_address1`), value(`${prefix}_address2`), value(`${prefix}_city`), value(`${prefix}_province`), value(`${prefix}_zip`), value(`${prefix}_country`)].filter(Boolean).join(', ');
    }

    function validate(p) {
      const errors = [];
      const required = [['name', 'Full name'], ['email', 'Email address'], ['phone', 'Phone number'], ['ship_address1', 'Shipping street address'], ['ship_city', 'Shipping city'], ['ship_province', 'Shipping state'], ['ship_zip', 'Shipping ZIP code'], ['ship_country', 'Shipping country']];
      if (!form.elements.billing_same.checked) required.push(['bill_address1', 'Billing street address'], ['bill_city', 'Billing city'], ['bill_province', 'Billing state'], ['bill_zip', 'Billing ZIP code'], ['bill_country', 'Billing country']);
      for (const [name, label] of required) if (!value(name)) errors.push(`${label} is required.`);
      if (value('email') && !EMAIL.test(value('email'))) errors.push('Enter a valid email address.');
      if (value('phone') && !PHONE.test(value('phone'))) errors.push('Enter a valid phone number.');
      if (!state.garment) errors.push('Choose a garment.');
      if (state.garment && state.garment.other && !value('other_garment')) errors.push('Describe the garment you want.');
      if (state.garment && !state.color) errors.push('Choose a color.');
      const min = p.mode === 'quote' ? Math.max(1, Number(config.minQuoteQty) || 1) : 1;
      if (totalQty() < min) errors.push(min === 1 ? 'Enter a quantity for at least one size.' : `Quote requests start at ${min} pieces.`);
      if (!state.method) errors.push('Choose a decoration method.');
      if (!state.placements.size) errors.push('Choose at least one placement.');
      if (!state.files.length && !form.elements.design_help.checked) errors.push('Upload your artwork, or tick "I need design help".');
      if (!form.elements.consent.checked) errors.push('Please confirm the artwork permission statement.');
      return errors;
    }

    function showErrors(errors) {
      const box = el('errors');
      if (!errors.length) { box.hidden = true; box.innerHTML = ''; return; }
      box.innerHTML = `<p>Please fix the following:</p><ul>${errors.map((e) => `<li>${escapeHtml(e)}</li>`).join('')}</ul>`;
      box.hidden = false;
      box.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function sharedProperties(id) {
      const props = {
        'Request ID': id,
        Garment: state.garment.other && value('other_garment') ? `${state.garment.title}: ${value('other_garment')}` : state.garment.title,
        Color: state.color,
        Decoration: state.method,
        Placement: [...state.placements].join(', ')
      };
      if (value('width_in') || value('height_in')) props['Design size'] = `${value('width_in') || '?'} in wide × ${value('height_in') || '?'} in tall`;
      if (value('needed_by')) props['Needed by'] = value('needed_by');
      if (value('instructions')) props.Instructions = value('instructions').slice(0, 1500);
      if (form.elements.design_help.checked) props['Design help'] = 'Requested';
      return props;
    }

    function sizeBreakdown() {
      return Object.entries(state.qty).filter(([, q]) => q > 0).map(([s, q]) => `${s} × ${q}`).join(', ');
    }

    async function postJson(url, body) {
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || `Request failed (${res.status})`);
      return data;
    }

    async function addWithFiles(variantId, quantity, properties) {
      const fd = new FormData();
      fd.append('id', String(variantId));
      fd.append('quantity', String(quantity));
      for (const [k, v] of Object.entries(properties)) fd.append(`properties[${k}]`, v);
      state.files.forEach((file, i) => fd.append(`properties[${i === 0 ? 'Artwork' : `Artwork ${i + 1}`}]`, file, file.name));
      const res = await fetch(config.cartAddUrl, { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || `Upload failed (${res.status})`);
      return data;
    }

    async function saveAttributes(id, type) {
      const attributes = {
        'Custom order request ID': id,
        'Custom order type': type,
        'Customer name': value('name'),
        'Customer email': value('email'),
        'Customer phone': value('phone'),
        'Shipping address': address('ship'),
        'Billing address': form.elements.billing_same.checked ? 'Same as shipping' : address('bill')
      };
      return postJson(config.cartUpdateUrl, { attributes });
    }

    function done(html) {
      const box = el('done');
      box.innerHTML = html;
      box.hidden = false;
      form.hidden = true;
      box.focus();
      box.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    async function submitPriced(p, id) {
      const lineItems = p.rows.filter((r) => r.qty > 0);
      const props = sharedProperties(id);
      const [first, ...rest] = lineItems;
      await addWithFiles(first.variant.id, first.qty, props);
      if (rest.length) {
        const restProps = { ...props };
        if (state.files.length) restProps.Artwork = `Attached to the ${first.size} item (${id})`;
        await postJson(config.cartAddUrl, { items: rest.map((r) => ({ id: r.variant.id, quantity: r.qty, properties: restProps })) });
      }
      await saveAttributes(id, 'Priced order');
      const cart = await (await fetch(config.cartJsUrl, { headers: { Accept: 'application/json' } })).json();
      const discounts = (cart.cart_level_discount_applications || []).map((d) => d.title).filter(Boolean);
      done(`
        <p class="ta-co__eyebrow">Request ${escapeHtml(id)}</p>
        <h2>Added to your cart.</h2>
        <dl class="ta-co__totals">
          <div><dt>Cart subtotal</dt><dd>${fmt(cart.original_total_price)}</dd></div>
          <div><dt>Discounts applied by Shopify</dt><dd>${cart.total_discount ? `− ${fmt(cart.total_discount)}` : 'None yet'}</dd></div>
          <div class="ta-co__totals-strong"><dt>Cart total</dt><dd>${fmt(cart.total_price)}</dd></div>
        </dl>
        ${discounts.length ? `<p class="ta-co__hint">Applied: ${discounts.map(escapeHtml).join(', ')}</p>` : ''}
        <p class="ta-co__hint">Shipping, taxes and discount codes are calculated at checkout.</p>
        <div class="ta-co__actions"><a class="ta-co__btn ta-co__btn--primary" href="${config.checkoutUrl}">Go to checkout</a><a class="ta-co__btn" href="${config.cartUrl}">View cart</a></div>`);
    }

    async function submitQuote(id) {
      const props = { ...sharedProperties(id), Sizes: sizeBreakdown(), 'Total pieces': String(totalQty()) };
      await addWithFiles(config.quoteVariantId, 1, props);
      await saveAttributes(id, 'Quote request');
      const cart = await (await fetch(config.cartJsUrl, { headers: { Accept: 'application/json' } })).json();
      const otherPaid = cart.total_price > 0;
      done(`
        <p class="ta-co__eyebrow">Request ${escapeHtml(id)}</p>
        <h2>One last step.</h2>
        <p>Confirm your request at checkout so we receive it with your artwork. The quote request is $0.00 — no card is needed for it.${otherPaid ? ' Your cart also has other items, which are charged as normal.' : ''}</p>
        <p class="ta-co__hint">You'll get an email confirmation and an order status page. We'll reply with your price; once you approve, we send a secure Shopify payment link.</p>
        <div class="ta-co__actions"><a class="ta-co__btn ta-co__btn--primary" href="${config.checkoutUrl}">Confirm request at checkout</a><a class="ta-co__btn" href="${config.cartUrl}">View cart</a></div>`);
    }

    function submitFallback(id) {
      const fb = root.querySelector('#ta-co-contact-fallback');
      if (!fb) throw new Error('The request form is unavailable. Please use our contact page.');
      const props = { ...sharedProperties(id), Sizes: sizeBreakdown(), 'Total pieces': String(totalQty()) };
      const body = [
        `Custom order quote request ${id}`,
        ...Object.entries(props).map(([k, v]) => `${k}: ${v}`),
        `Shipping address: ${address('ship')}`,
        `Billing address: ${form.elements.billing_same.checked ? 'Same as shipping' : address('bill')}`,
        `Artwork files: ${state.files.length ? state.files.map((f) => f.name).join(', ') + ' (customer will email files)' : 'None (design help requested)'}`
      ].join('\n');
      fb.querySelector('[name="contact[name]"]').value = value('name');
      fb.querySelector('[name="contact[email]"]').value = value('email');
      fb.querySelector('[name="contact[phone]"]').value = value('phone');
      fb.querySelector('[name="contact[body]"]').value = body;
      fb.submit();
    }

    form.addEventListener('change', (event) => {
      const t = event.target;
      if (t.name === 'garment') { state.garment = garmentById(t.value); renderGarment(); }
      else if (t.name === 'color') state.color = t.value;
      else if (t.name === 'method') state.method = t.value;
      else if (t.name === 'placement') { if (t.checked) state.placements.add(t.value); else state.placements.delete(t.value); renderPreview(); }
      else if (t.matches('[data-ta-co-billing-toggle]')) el('billing').hidden = t.checked;
      else if (t.matches('[data-ta-co-files]')) {
        const maxFiles = Number(t.dataset.maxFiles) || 3;
        const maxBytes = (Number(t.dataset.maxMb) || 20) * 1048576;
        const picked = [...t.files];
        const problems = [];
        state.files = picked.filter((f) => {
          if (!FILE_EXT.test(f.name)) { problems.push(`${f.name}: use PNG, JPG, WebP, SVG or PDF.`); return false; }
          if (f.size > maxBytes) { problems.push(`${f.name}: larger than ${t.dataset.maxMb} MB.`); return false; }
          return true;
        }).slice(0, maxFiles);
        if (picked.length > maxFiles) problems.push(`Only the first ${maxFiles} files will be sent.`);
        showErrors(problems);
        renderFiles();
      }
      renderPricing();
    });

    form.addEventListener('input', (event) => {
      const t = event.target;
      if (t.dataset && t.dataset.size) {
        const n = Math.max(0, Math.min(500, Math.floor(Number(t.value) || 0)));
        state.qty[t.dataset.size] = n;
        renderPricing();
      }
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const p = renderPricing();
      const errors = validate(p);
      showErrors(errors);
      if (errors.length) return;
      const id = requestId();
      if (value('ta_website')) { done('<h2>Request received.</h2>'); return; }
      const submit = el('submit');
      const label = submit.textContent;
      submit.disabled = true;
      submit.textContent = state.files.length ? 'Uploading artwork…' : 'Saving…';
      try {
        if (p.mode === 'priced') await submitPriced(p, id);
        else if (quoteProductReady) await submitQuote(id);
        else submitFallback(id);
      } catch (error) {
        showErrors([error.message || 'Something went wrong. Please try again.']);
        submit.disabled = false;
        submit.textContent = label;
      }
    });

    renderPricing();
  }

  const boot = () => document.querySelectorAll('[data-ta-custom-order]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', (event) => init(event.target.querySelector('[data-ta-custom-order]')));
})();
